/**
 * Entry point: wires core systems, PiP, panels, and Phaser together.
 *
 * Architecture:
 *   core/  — pure logic, zero DOM/Phaser (the source of truth)
 *   ui/    — HTML/CSS panels + PiP (read state, dispatch via core functions)
 *   scenes/— Phaser rendering (subscribes to combat:* events on the bus)
 *
 * Landing-page flow:
 *   - Site loads → landing page visible, PiP hidden.
 *   - Click "Jogar Agora" → PiP appears, game starts.
 *   - Returning players (heroChosen=true) skip the landing page.
 */

import Phaser from 'phaser';
import { loadGame, saveGame } from './core/save.js';
import { GameLoop } from './core/gameLoop.js';
import { setParty } from './core/heroes.js';
import { bus } from './core/events.js';
import { BootScene } from './scenes/BootScene.js';
import { GameScene } from './scenes/GameScene.js';

// UI layer
import { getSession, showLoginScreen, startCloudSync } from './ui/auth.js';
import { initSfx, isMuted, setMuted } from './ui/sfx.js';
import { createPiP } from './ui/pip.js';
import { createPiPWindowManager } from './ui/pip-window.js';
import { panelManager } from './ui/panels/PanelManager.js';
import { HeroPanel } from './ui/panels/HeroPanel.js';
import { StatusPanel } from './ui/panels/StatusPanel.js';
import { PortalPanel } from './ui/panels/PortalPanel.js';
import { CubePanel } from './ui/panels/CubePanel.js';
import { ShopPanel } from './ui/panels/ShopPanel.js';
import { AccountPanel } from './ui/panels/AccountPanel.js';
import { initBackground } from './ui/background.js';
import { initSite } from './ui/site.js';
import { initConsumablesBar } from './ui/consumables.js';

// Balance tables
import classes from './data/classes.json';
import maps from './data/maps.json';
import items from './data/items.json';
import rarity from './data/rarity.json';
import affixes from './data/affixes.json';
import loot from './data/loot.json';
import balance from './data/balance.json';
import skills from './data/skills.json';
import dungeons from './data/dungeons.json';

const data = { classes, maps, items, rarity, affixes, loot, balance, skills, dungeons };

// ==========================================================================
// 1. Load (or create) the save. There is NO offline progress by design: the
//    dungeon only fights while the site (or the PiP window) is actually open —
//    that's the whole point of the pocket-window game.
// ==========================================================================

const state = loadGame();

if (!data.classes[state.party[0]?.classId]) {
  state.party[0].classId = 'knight';
  state.heroChosen = false;
}

const loop = new GameLoop(state, data);

// Chiptune SFX: synthesized on the fly from combat bus events (see ui/sfx.js).
initSfx();
document.querySelectorAll('[data-sound]').forEach((btn) => {
  const sync = (m) => btn.classList.toggle('pd-sound-off', m);
  sync(isMuted());
  btn.addEventListener('click', () => setMuted(!isMuted()));
  bus.on('sfx:muted', sync);
});

// ==========================================================================
// 2. Autosave: debounced on state changes + on tab hide/unload.
// ==========================================================================

let saveTimer = null;
bus.on('state:dirty', () => {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => saveGame(state), 5000);
});

const flush = () => saveGame(state);
window.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flush();
});
window.addEventListener('beforeunload', flush);

// ==========================================================================
// 3. Panel system — create early (needed for hero selection).
// ==========================================================================

const hooks = {
  onChange: flush,
  onChooseParty: null, // set after startGame
};

const heroPanel = new HeroPanel({ state, data, hooks });
const statusPanel = new StatusPanel({ state, data, hooks });
const portalPanel = new PortalPanel({ state, data, hooks });
const cubePanel = new CubePanel({ state, data, hooks });
const shopPanel = new ShopPanel({ state, data, hooks });
const accountPanel = new AccountPanel({ state, data, hooks });

panelManager.register('hero', heroPanel);
panelManager.register('status', statusPanel);
panelManager.register('portal', portalPanel);
panelManager.register('cube', cubePanel);
panelManager.register('shop', shopPanel);
panelManager.register('account', accountPanel);

// ==========================================================================
// 4. Background site + particles (always visible).
// ==========================================================================

initSite(data);
initBackground('pd-particles');

// Consumable stock counters on the AÇÕES toolbar (apple/strawberry/meat/kit).
initConsumablesBar(document.getElementById('pd-consumables'), state, data);

// ==========================================================================
// 5. startGame — called when the user clicks "Jogar Agora" (or auto for
//    returning players). Shows the PiP embedded in the site, creates Phaser,
//    starts the loop. The ⊡ button detaches to native browser PiP.
// ==========================================================================

let game = null;
let pip = null;
let pipWin = null;
let loopStarted = false;

function startGame() {
  if (game) return;

  const pipEl = document.getElementById('pd-pip');
  if (!pipEl) return;

  // Reveal the game section + PiP (both hidden until the game starts)
  document.getElementById('pd-section-game')?.removeAttribute('hidden');
  pipEl.removeAttribute('hidden');

  // Scroll to the game section
  const gameSection = document.getElementById('pd-section-game');
  if (gameSection) {
    setTimeout(() => {
      gameSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }

  // ---- PiP API for cross-window panel control ----
  const pipAPI = {
    openPanel(name) {
      if (panelManager.isOpen(name)) {
        panelManager.close();
      } else {
        panelManager.open(name);
      }
      window.focus();
    },
  };

  // ---- Native PiP window manager ----
  pipWin = createPiPWindowManager();

  // ---- Embedded PiP ----
  pip = createPiP({
    callbacks: {
      /** ⊡ clicked: open native PiP if supported, else stay as floating window. */
      onPopOut: async () => {
        const canvas = document.querySelector('#pd-stage canvas');
        if (!canvas) return;

        if (!pipWin.isSupported) {
          // Keep as floating window — user can still drag it around
          pip.enterFloating();
          return;
        }

        // Relocate the whole panels container into the PiP window so the
        // action panels open *inside* the PiP (fully styled + interactive),
        // then move it back home when the PiP closes.
        const panelsEl = document.getElementById('pd-panels');
        const panelsHome = panelsEl?.parentNode;

        let pipConsumables = null;
        const ok = await pipWin.open(canvas, {
          onReady: (pipWindow) => {
            const stage = pipWindow.document.querySelector('.pd-pipw-stage');
            if (panelsEl && stage) stage.appendChild(panelsEl);
            // Live consumable counters in the native PiP toolbar too.
            const pipBar = pipWindow.document.getElementById('pd-consumables-pip');
            pipConsumables = initConsumablesBar(pipBar, state, data);
          },
          onClose: () => {
            // Move panels back BEFORE the PiP document is torn down, then
            // return the embedded game to the site.
            pipConsumables?.destroy();
            pipConsumables = null;
            panelManager.close();
            if (panelsEl && panelsHome) panelsHome.appendChild(panelsEl);
            if (pip) {
              pip.exitNativeMode();
              pip.exitFloating();
            }
          },
          onAction: (action) => {
            // Panels now live inside the PiP window — open/toggle directly.
            // (No window.focus(): that would steal focus back to the main tab.)
            if (panelManager.isOpen(action)) panelManager.close();
            else panelManager.open(action);
          },
        });

        if (ok) {
          pip.enterNativeMode();
        } else {
          // Native PiP failed → stay as floating window
          pip.enterFloating();
        }
      },

      /** ⊟ clicked: close native PiP, return to embedded. */
      onPopIn: () => {
        if (pipWin) pipWin.close();
        // exitNativeMode + exitFloating called by onClose above
      },
    },
  });

  // ---- Phaser ----
  game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'pd-stage',
    width: 320,
    height: 200,
    pixelArt: true,
    backgroundColor: '#1a1620',
    render: { preserveDrawingBuffer: true },
    scene: [BootScene, GameScene],
    scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
  });

  game.registry.set('state', state);
  game.registry.set('data', data);

  // Keep the game canvas scaled to the PiP at all times. Phaser's FIT mode only
  // reacts to window 'resize', which doesn't fire while the user drags the PiP
  // resize handle or as the embedded PiP reflows — so observe the stage element
  // directly and refit on every size change.
  if (window.ResizeObserver) {
    const stageEl = document.getElementById('pd-stage');
    if (stageEl) {
      const ro = new ResizeObserver(() => game.scale?.refresh());
      ro.observe(stageEl);
    }
  }

  // ---- Wire "Voltar ao site" placeholder button ----
  document.getElementById('pd-restore-btn')?.addEventListener('click', () => {
    if (pipWin) pipWin.close();
  });

  window.addEventListener('pd:pip-closed', () => {
    if (pip && pip.poppedOut) {
      pip.exitNativeMode();
      pip.exitFloating();
    }
  });

  // Highlight active panel's button (PiP toolbar under the ☰ menu)
  bus.on('panel:opened', ({ name }) => {
    document.querySelectorAll('.pd-pip-toolbar [data-panel]').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.panel === name);
    });
  });
  bus.on('panel:closed', () => {
    document.querySelectorAll('.pd-pip-toolbar [data-panel]').forEach((btn) => {
      btn.classList.remove('active');
    });
    // First-launch hero select can now be dismissed with the ✕. If the player
    // closes it without confirming a party, fall back to the default party so the
    // game actually starts instead of sitting frozen with no fight running.
    if (!state.heroChosen) {
      chooseParty(state.party.map((h) => h.classId));
    }
  });

  // ---- Panel open from PiP toolbar (CustomEvent fallback) ----
  window.addEventListener('pd:open-panel', (e) => {
    const name = e.detail?.name;
    if (!name) return;
    if (panelManager.isOpen(name)) {
      panelManager.close();
    } else {
      panelManager.open(name);
    }
  });

  // ---- Bus events ----
  // Clicking a hero in the game view (sprite, HUD bar or drop card) opens the
  // HERÓI panel focused on that exact hero, AND arms the STATUS panel on the
  // same hero — so switching to STATUS shows the clicked character's skills.
  // Without a heroIndex (backpack icon) the panels keep their last hero.
  bus.on('ui:openHero', ({ heroIndex } = {}) => {
    if (heroIndex != null) {
      heroPanel.focusHero(heroIndex);
      statusPanel.focusHero(heroIndex, statusPanel.activeTab === 'skills' ? 'skills' : 'overview');
    }
    panelManager.open('hero');
  });
  // "+" on the consumables bar opens the gold-only shop (was a Cube tab).
  bus.on('ui:openShop', () => {
    if (panelManager.isOpen('shop')) panelManager.close();
    else panelManager.open('shop');
  });
  bus.on('ui:changeMap', ({ mapId }) => {
    if (!data.maps[mapId]) return;
    state.activeMapId = mapId;
    state.currentMonsters = [];
    state.killsSinceBoss = 0; // fresh boss countdown on the new map
    flush();
    game.scene.stop('Game');
    game.scene.start('Game', { state, data });
    panelManager.close();
  });

  // ---- Party selection (1 or 2 heroes; each class levels independently) ----
  function chooseParty(classIds) {
    setParty(state, classIds, data); // restores/creates each hero (no free revive)
    // Keep the current monster pack: swapping in a hero (e.g. after one fell)
    // should NOT reset the fight's monster count or their current HP. The pack is
    // only cleared when you actually travel to a different map (ui:changeMap).
    state.heroChosen = true;
    flush();
    game.scene.stop('Game');
    game.scene.start('Game', { state, data });
    if (!loopStarted) {
      loop.start();
      loopStarted = true;
    }
  }

  hooks.onChooseParty = chooseParty;

  // ---- Start ----
  if (state.heroChosen) {
    loop.start();
    loopStarted = true;
  } else {
    // Open on the hero-select tab, but keep it closable (the ✕ works now).
    heroPanel.prepareForcedSelect();
    panelManager.open('hero');
  }

  console.log('PocketDungeon: pronto.', state);

  // ---- Debug ----
  window.PD = {
    state, loop, game, pip, pipWin,
    pipAPI,
    panels: panelManager,
    save: flush,
    heroPanel,
  };
}

// ==========================================================================
// 6. Wire "Jogar Agora" CTA (shortcut to activate PiP) and nav "Jogar" link.
// ==========================================================================

/**
 * Once the game client is open, the big hero CTA has no job left — hide it
 * (the nav "Jogar" stays as a shortcut that scrolls to the running game).
 */
function markGameOpen() {
  document.querySelector('.pd-site-game-cta')?.classList.add('pd-cta-hidden');
  showPipHintOnce();
}

/**
 * One-time onboarding hint: the floating window IS the game loop (there's no
 * offline progress), so teach the ⊡ button the first time the game opens.
 */
function showPipHintOnce() {
  try {
    if (localStorage.getItem('pocketdungeon.hint.pip')) return;
    localStorage.setItem('pocketdungeon.hint.pip', '1');
  } catch { return; }

  // Wait for the smooth-scroll to settle so the bubble lands where the button is.
  setTimeout(() => {
    const btn = document.querySelector('.pd-pip-popout');
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const tip = document.createElement('div');
    tip.className = 'pd-pip-hint';
    tip.innerHTML = '💡 Clique em <b>⊡</b> para destacar a masmorra numa <b>janelinha flutuante</b> ' +
      'e continue usando o computador. O herói só luta com o jogo aberto — fechar pausa o mundo.';
    tip.style.top = `${r.bottom + 10}px`;
    tip.style.left = `${Math.max(12, r.right - 260)}px`;
    document.body.appendChild(tip);
    const dismiss = () => { tip.remove(); document.removeEventListener('pointerdown', dismiss); };
    setTimeout(dismiss, 14000);
    setTimeout(() => document.addEventListener('pointerdown', dismiss), 400);
  }, 1600);
}

async function handlePlayClick(e) {
  e.preventDefault();
  // Account gate: playing requires a login (e-mail + senha). The session is a
  // 30-day token; the cloud save follows the account across devices.
  if (!getSession()) {
    const outcome = await showLoginScreen(); // 'ok' | 'cancelled' (reloads on cloud adopt)
    if (outcome === 'cancelled' || !getSession()) return; // closed the login — stay on the site
  }
  startCloudSync();
  // If PiP is popped out, bring it back first
  if (pip && pipWin && pipWin.isActive) {
    pipWin.close();
  }
  if (pip && pip.floating) {
    pip.exitFloating();
  }
  startGame();
  markGameOpen();
  // Smooth-scroll to the game section
  const gameSection = document.getElementById('pd-section-game');
  if (gameSection) {
    gameSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

document.querySelector('.pd-site-game-cta')?.addEventListener('click', handlePlayClick);
document.querySelector('.pd-site-nav-links a[href="#pd-pip"]')?.addEventListener('click', handlePlayClick);

// ==========================================================================
// 7. Auto-start for returning players (only with a live session — logged-out
//    visitors always pass through the login screen via "Jogar Agora").
// ==========================================================================

if (state.heroChosen && getSession()) {
  startCloudSync();
  startGame();
  markGameOpen();
}
